import { blue, neutral } from "@flat-ui";
import { LineChartPro } from "@mui/x-charts-pro/LineChartPro";
import { AnalyticsChartProps } from "@components/analytics/in-house/constants/analytics-constants";
import { shouldRenderXTickLabel } from "@components/analytics/in-house/utils/analytics-utils";

/** Renders the line variant for in-house analytics data. */
export function AnalyticsLineChart({
  dataset,
  xTickStep,
  yAxisLabel,
  seriesUnitLabel,
}: AnalyticsChartProps): JSX.Element {
  const lineSeriesData = dataset.map(({ value }) => (value > 0 ? value : null));

  return (
    <>
      {/* eslint-disable @typescript-eslint/naming-convention -- MUI X chart API uses third-party prop names */}
      <LineChartPro
        height={360}
        margin={{ top: 32, right: 32, bottom: 70, left: 70 }}
        xAxis={[
          {
            scaleType: "point",
            data: dataset.map((item) => item.label),
            tickLabelInterval: (_value: string, index: number) =>
              shouldRenderXTickLabel(index, dataset.length, xTickStep),
            disableLine: true,
            disableTicks: true,
            tickLabelStyle: {
              fontSize: 14,
              fill: neutral[600],
            },
          },
        ]}
        yAxis={[
          {
            min: 0,
            label: yAxisLabel,
            tickMinStep: 1,
            valueFormatter: (value) => `${Math.round(value)}`,
            disableLine: true,
            disableTicks: true,
            labelStyle: {
              fill: neutral[600],
            },
            tickLabelStyle: {
              fontSize: 14,
              fill: neutral[600],
              transform: "translate(10px, 2px)",
            },
          },
        ]}
        grid={{ horizontal: true, vertical: false }}
        sx={{
          "&, .MuiLineChart-root": {
            border: "none",
            outline: "none",
            boxShadow: "none",
            backgroundColor: "transparent",
          },
          ".MuiChartsAxis-root .MuiChartsAxis-tickLabel": {
            transform: "translateY(2px)",
          },
          ".MuiChartsAxis-line": {
            stroke: "transparent",
          },
          ".MuiChartsAxis-tick": {
            stroke: "transparent",
          },
          ".MuiChartsGrid-line": {
            stroke: neutral[300],
            strokeDasharray: "2 5",
          },
          ".MuiLineElement-root": {
            strokeWidth: 2,
          },
          ".MuiMarkElement-root": {
            fill: neutral[0],
            stroke: blue[400],
            strokeWidth: 2,
          },
        }}
        series={[
          {
            data: lineSeriesData,
            color: blue[400],
            connectNulls: true,
            curve: "linear",
            label: undefined,
            type: "line",
            showMark: true,
            valueFormatter: (value) => `${value ?? 0} ${seriesUnitLabel}`,
            highlightScope: { highlight: "none", fade: "none" },
          },
        ]}
      />
      {/* eslint-enable @typescript-eslint/naming-convention */}
    </>
  );
}
